import React from 'react';

/**
 * A Trust Score, as a wheel.
 *
 * <h3>Why the score is drawn rather than written</h3>
 *
 * The trail used to state it in a sentence — "Scored 57 (Limited) at design
 * time" — which reads as one more line of narration in a column of narration.
 * The score is the thing the whole journey is for: it is the number the agent
 * carries afterwards, and the one a reviewer looks for first. Drawn, it is
 * found at a glance and the band is legible without reading.
 *
 * <h3>Band, not a gradient</h3>
 *
 * The arc takes the band's colour, not a continuous hue ramp. The platform
 * already decides the band server-side and renders it that way everywhere
 * else; a second, prettier opinion here would be a second answer to "is this
 * score good", which is exactly the kind of drift this codebase keeps paying
 * for. An unrated agent gets a hollow ring and the word, never a zero — zero
 * is a score, "not yet scored" is not.
 */
const BAND_TOKEN = {
  strong: 'var(--p7-allow, #16a34a)',
  medium: 'var(--p7-hold, #d97706)',
  limited: 'var(--p7-hold, #d97706)',
  at_risk: 'var(--p7-deny, #dc2626)',
  unrated: 'var(--p7-line-strong, #cbd5e1)',
};

export default function TrustWheel({
  score = null, band = null, size = 72, label = 'Trust Score', caption = null,
}) {
  const rated = typeof score === 'number' && Number.isFinite(score);
  const key = String(band || (rated ? '' : 'unrated')).toLowerCase().replace(/\s+/g, '_');
  const colour = BAND_TOKEN[key] || BAND_TOKEN.unrated;

  const stroke = Math.max(5, Math.round(size * 0.1));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  /* Clamped: a score outside 0..100 is a backend bug, and a wheel that winds
     past full would hide it rather than show it. */
  const pct = rated ? Math.max(0, Math.min(100, score)) / 100 : 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
           role="img"
           aria-label={rated ? `${label}: ${score} out of 100, ${band || 'unrated'}`
                             : `${label}: not yet scored`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke="var(--p7-line-soft, #e2e8f0)" strokeWidth={stroke} />
        {rated ? (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none"
                  stroke={colour} strokeWidth={stroke} strokeLinecap="round"
                  strokeDasharray={`${c * pct} ${c}`}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        ) : null}
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
              style={{ fontFamily: 'var(--p7-font-sans)', fontWeight: 700,
                       fontSize: rated ? size * 0.3 : size * 0.17,
                       fill: rated ? 'var(--p7-ink)' : 'var(--p7-ink-subtle)' }}>
          {rated ? Math.round(score) : '—'}
        </text>
      </svg>
      <div style={{ minWidth: 0 }}>
        <div style={{ font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                      fontWeight: 600, color: 'var(--p7-ink)' }}>
          {label}
          {band ? (
            <span style={{ marginLeft: '8px', font: 'var(--p7-text-caption)',
                           fontFamily: 'var(--p7-font-sans)', fontWeight: 600,
                           color: colour, textTransform: 'capitalize' }}>
              {String(band).replace(/_/g, ' ')}
            </span>
          ) : null}
        </div>
        {caption ? (
          <div style={{ marginTop: '2px', font: 'var(--p7-text-caption)',
                        fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
            {caption}
          </div>
        ) : null}
      </div>
    </div>
  );
}
